/**
 * Captures UTM + Google Ads click identifiers from the URL and stores them
 * in sessionStorage (last-touch) + localStorage (first-touch) for the quiz
 * flow + intake submit + Stripe metadata.
 *
 * Idempotent: safe to call multiple times. Only writes when the current URL
 * has at least one tracked param, so it never blanks out a previously stored
 * value with empty querystring entries. Querystring values always win over
 * cached storage when present (Ads landings refresh the campaign tag).
 *
 * Captured keys:
 *   gclid, gbraid, wbraid — Google Ads click identifiers
 *   utm_source, utm_medium, utm_campaign, utm_content, utm_term
 *   landing_url, referrer, captured_at (added meta)
 *
 * Privacy: NEVER captures email, phone or other PII here. Click-ids stay
 * server-side only — we do NOT forward them to Google in this build.
 */
import { getDeviceBucket } from '@/utils/deviceBucket';

const ATTR_KEYS = [
  'gclid', 'gbraid', 'wbraid',
  'utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term',
];
const FIRST_TOUCH_KEY = 'powersure_first_touch';
const LAST_TOUCH_KEY = 'powersure_attribution';   // last-touch (replaces legacy powersure_utm)
const LEGACY_UTM_KEY = 'powersure_utm';            // backwards compat read

function _nowIso() {
  try { return new Date().toISOString(); } catch { return ''; }
}

export function captureUtmParams() {
  if (typeof window === 'undefined') return {};
  const params = new URLSearchParams(window.location.search);
  const captured = {};
  let hasAny = false;
  for (const key of ATTR_KEYS) {
    const val = params.get(key);
    if (val) {
      captured[key] = val;
      hasAny = true;
    }
  }
  if (!hasAny) return getStoredUtm();

  // Enrich with landing context (no PII)
  captured.landing_url = window.location.href.split('#')[0];
  captured.referrer = (document.referrer || '').split('#')[0];
  captured.captured_at = _nowIso();

  try {
    // Last-touch always overwrites.
    sessionStorage.setItem(LAST_TOUCH_KEY, JSON.stringify(captured));
    // Legacy mirror for older code paths that still read powersure_utm.
    sessionStorage.setItem(LEGACY_UTM_KEY, JSON.stringify(captured));
    // First-touch only writes if empty.
    if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
      localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(captured));
    }
  } catch { /* storage may be unavailable */ }
  return captured;
}

// Lightweight one-shot capture used by trackFunnelEvent before the first
// event leaves the page. This guarantees the very first child-mount
// `intake_started` carries `utm_campaign` even though App.js's parent
// useEffect has not run yet.
let _utmCapturedOnce = false;
function ensureUtmCaptured() {
  if (_utmCapturedOnce) return;
  _utmCapturedOnce = true;
  captureUtmParams();
}

export function getStoredUtm() {
  try {
    const last = JSON.parse(sessionStorage.getItem(LAST_TOUCH_KEY) || 'null');
    if (last) return last;
    // Fallback to legacy key if last-touch is empty (e.g., older sessions).
    return JSON.parse(sessionStorage.getItem(LEGACY_UTM_KEY) || '{}');
  } catch {
    return {};
  }
}

export function getFirstTouchUtm() {
  try { return JSON.parse(localStorage.getItem(FIRST_TOUCH_KEY) || '{}'); }
  catch { return {}; }
}

// Returns a flat attribution object suitable for embedding in any backend
// payload. Combines last-touch (primary) + first-touch (fallback) so that
// returning Ads visitors don't lose their original campaign tag.
export function getAttribution() {
  const last = getStoredUtm() || {};
  const first = getFirstTouchUtm() || {};
  return {
    // click-ids
    gclid: last.gclid || first.gclid || '',
    gbraid: last.gbraid || first.gbraid || '',
    wbraid: last.wbraid || first.wbraid || '',
    // utm
    utm_source: last.utm_source || first.utm_source || '',
    utm_medium: last.utm_medium || first.utm_medium || '',
    utm_campaign: last.utm_campaign || first.utm_campaign || '',
    utm_content: last.utm_content || first.utm_content || '',
    utm_term: last.utm_term || first.utm_term || '',
    // context
    landing_url: last.landing_url || first.landing_url || '',
    referrer: last.referrer || first.referrer || '',
    first_touch_at: first.captured_at || '',
    last_touch_at: last.captured_at || '',
  };
}

export function trackFunnelEvent(event, extra = {}) {
  // Make sure UTMs are persisted in sessionStorage before we read them.
  // Idempotent: only runs the URL parse once per page load.
  ensureUtmCaptured();
  const attr = getAttribution();
  // Optional event-specific context. Caller may pass `extra.meta` as a plain
  // object (e.g. { product_id, cta_label, segment }). The backend runs
  // sanitize() over the whole document; do not put email, affiliate_url,
  // tokens or other PII / secrets in here.
  const meta = (extra && typeof extra.meta === 'object' && extra.meta !== null) ? { ...extra.meta } : {};
  // Mirror click-ids into meta so legacy collectors that only inspect meta
  // still see the attribution.
  if (attr.gclid && !meta.gclid) meta.gclid = attr.gclid;
  if (attr.gbraid && !meta.gbraid) meta.gbraid = attr.gbraid;
  if (attr.wbraid && !meta.wbraid) meta.wbraid = attr.wbraid;
  // Device bucket — promoted to top-level so analytics can split
  // mobile/tablet/desktop drop-off per event without parsing meta.
  let deviceType = '';
  try { deviceType = getDeviceBucket(); } catch { /* SSR safety */ }
  fetch(`/api/track/funnel`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      event,
      report_id: extra.report_id || '',
      step: extra.step || null,
      country: extra.country || '',
      session_id: sessionStorage.getItem('powersure_session') || '',
      // Attribution — full UTM set so backend analytics can join
      // Ads → funnel → payment without depending on meta blob.
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      utm_content: attr.utm_content,
      utm_term: attr.utm_term,
      // New: pass gclid through as a top-level field so backend analytics
      // can join payment_completed with ad-click without depending on meta.
      gclid: attr.gclid,
      gbraid: attr.gbraid,
      wbraid: attr.wbraid,
      // Landing context (no PII)
      landing_url: attr.landing_url || '',
      referrer: attr.referrer || '',
      device_type: deviceType,
      flow: extra.flow || '',
      intake_id: extra.intake_id || '',
      meta: Object.keys(meta).length ? meta : null,
    }),
  }).catch(() => {});
}

// Generate a session ID for funnel tracking
if (typeof window !== 'undefined' && !sessionStorage.getItem('powersure_session')) {
  sessionStorage.setItem('powersure_session', `ps-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
}
